import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { AlertCircle, Loader2, Lock, Mail, Shield } from "lucide-react";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { GoogleSignInButton } from "@/components/google-signin-button";
import { authService } from "@/lib/services/authService";
import { loginSchema, friendlyAuthError } from "@/lib/auth-validation";


type Search = { redirect?: string; staff?: boolean };

export const Route = createFileRoute("/login")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    redirect: typeof s.redirect === "string" ? s.redirect : undefined,
    staff: s.staff === true || s.staff === "true",
  }),
  head: () => ({
    meta: [
      { title: "ClaimGuard | Sign in" },
      { name: "description", content: "Sign in to your ClaimGuard workspace." },
    ],
  }),
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const { redirect, staff } = Route.useSearch();
  const [staffMode, setStaffMode] = useState(!!staff);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const submitInFlight = useRef(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitInFlight.current) return;
    setError(null);
    setFieldErrors({});
    const parsed = loginSchema.safeParse({ email, password });
    if (!parsed.success) {
      const fe: Record<string, string> = {};
      parsed.error.issues.forEach((i) => (fe[i.path[0] as string] = i.message));
      setFieldErrors(fe);
      return;
    }
    submitInFlight.current = true;
    setSubmitting(true);
    try {
      const { data, error: err } = await authService.signInWithPassword(
        parsed.data.email,
        parsed.data.password,
      );
      if (err || !data.user) throw err ?? new Error("Sign in failed");
      const roles = await authService.getRoles(data.user.id);

      // Route based on role. Redirect param is honored when the destination
      // matches the user's role scope.
      const landing = authService.landingPathForRoles(roles);
      const target = redirect && redirect.startsWith("/") ? redirect : landing;
      navigate({ to: target });
    } catch (err) {
      console.error("[auth] sign-in failed:", err);
      setError(friendlyAuthError(err));
    } finally {
      setSubmitting(false);
      submitInFlight.current = false;
    }
  }

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <SiteHeader />
      <main className="flex-1">
        <div className="container-page grid place-items-center py-14">
          <div className="w-full max-w-md rounded-2xl border bg-card p-8 shadow-sm">
            <div className="mb-6 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-navy text-navy-foreground">
                <Shield className="h-5 w-5" />
              </div>
              <div>
                <h1 className="text-xl font-semibold tracking-tight">
                  {staffMode ? "Staff sign in" : "Welcome back"}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {staffMode
                    ? "Sign in to the operations workspace."
                    : "Sign in to see your reviews."}
                </p>
              </div>
            </div>

            {error && (
              <div className="mb-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={onSubmit} className="space-y-4" noValidate>
              <Field
                icon={<Mail className="h-4 w-4" />}
                label="Email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={setEmail}
                error={fieldErrors.email}
              />
              <Field
                icon={<Lock className="h-4 w-4" />}
                label="Password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={setPassword}
                error={fieldErrors.password}
              />
              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center gap-2 text-muted-foreground">
                  <input type="checkbox" defaultChecked className="h-4 w-4 rounded border-input" />
                  Remember me
                </label>
                <Link to="/forgot-password" className="font-medium text-navy hover:underline">
                  Forgot password?
                </Link>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex w-full items-center justify-center rounded-md bg-navy px-4 py-2.5 text-sm font-semibold text-navy-foreground shadow-sm transition hover:opacity-90 disabled:opacity-60"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
              </button>
            </form>

            {!staffMode && (
              <div className="mt-5">
                <div className="relative my-3 flex items-center gap-3 text-[11px] uppercase tracking-wider text-muted-foreground">
                  <div className="h-px flex-1 bg-border" />
                  or
                  <div className="h-px flex-1 bg-border" />
                </div>
                <GoogleSignInButton redirect={redirect} onError={setError} />
              </div>
            )}

            <div className="mt-6 border-t pt-4 text-center text-sm text-muted-foreground">
              {staffMode ? (
                <>
                  Not a staff member?{" "}
                  <button
                    onClick={() => setStaffMode(false)}
                    className="font-medium text-navy hover:underline"
                  >
                    Customer sign in
                  </button>
                </>
              ) : (
                <>
                  Don&apos;t have an account?{" "}
                  <Link to="/register" className="font-medium text-navy hover:underline">
                    Create one
                  </Link>
                  <div className="mt-3 text-xs">
                    <button
                      onClick={() => setStaffMode(true)}
                      className="text-muted-foreground hover:text-navy hover:underline"
                    >
                      Staff sign in →
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

function Field({
  icon,
  label,
  type,
  value,
  onChange,
  error,
  autoComplete,
}: {
  icon: React.ReactNode;
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  autoComplete?: string;
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium">{label}</label>
      <div
        className={`flex items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm focus-within:ring-2 focus-within:ring-navy/20 ${
          error ? "border-destructive" : "border-input"
        }`}
      >
        <span className="text-muted-foreground">{icon}</span>
        <input
          type={type}
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent outline-none"
        />
      </div>
      {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
    </div>
  );
}
